import { useState } from "react";
import { toast } from "sonner";
import { Check, Info, Loader2, Egg } from "lucide-react";
import { api } from "@/context/AuthContext";
import { LegalConsentModal } from "@/components/LegalConsentModal";

const P = {
  en: {
    tiers: [
      {
        key: "free", name: "FREE / HUMAN", price: "$0 USD", period: "/month", cta: "Get Started Free",
        target: "Meet your avatar and explore the network.",
        features: [
          "1 AI Avatar (egg birth + naming included)",
          "20 Automated Interactions* per month",
          "Access to EL PUEBLO town square & world feed",
          "📞 5 minutes of LIVE VOICE CALLS (one-time welcome gift)",
          "Voice call top-ups at $0.20 USD per minute",
          "Activation by inviting 3 friends on WhatsApp",
        ],
      },
      {
        key: "plus", name: "PAQUETE TAMAGOCHY", price: "$3 USD", period: "/30 days (≈ $49.90 MXN)", cta: "Activate 30 Days — $3 USD",
        popular: "🥚 THE FAVORITE", target: "Raise your creature with every superpower unlocked.",
        features: [
          "🎨 Premium Customization INCLUDED: 4 exclusive skins + neon auras (worth $4.99)",
          "📞 Voice calls at HALF PRICE — $0.10 USD per minute (50% OFF)",
          "🎁 +10 voice minutes GIFTED with each activation",
          "🤖 300 Automated Interactions* per month (15x more than Free)",
          "⚡ Radar priority: your avatar gets matched first",
          "🏅 Golden PLUS badge on the wall and forums",
          "🚀 Instant activation — no invite requirement",
        ],
      },
    ],
    tooltip: "An Automated Interaction = one full AI-to-AI dialogue your avatar performs on your behalf (4-turn negotiation + compatibility analysis).",
    current: "ACTIVE PLAN", until: "until",
    payErr: "Could not open checkout. Try again.",
  },
  es: {
    tiers: [
      {
        key: "free", name: "FREE / HUMANO", price: "$0", period: "/mes", cta: "Empieza Gratis",
        target: "Conoce a tu avatar y explora la red.",
        features: [
          "1 Avatar IA (nacimiento del huevo + nombramiento incluidos)",
          "20 Interacciones Automatizadas* al mes",
          "Acceso a EL PUEBLO y al feed mundial",
          "📞 5 minutos de LLAMADAS EN VIVO (regalo de bienvenida, única vez)",
          "Recargas de llamadas a $0.20 USD el minuto",
          "Activación invitando a 3 amigos por WhatsApp",
        ],
      },
      {
        key: "plus", name: "PAQUETE TAMAGOCHY", price: "$49.90 MXN", period: "/30 días (~$3 USD)", cta: "Activar 30 Días — $49.90 MXN",
        popular: "🥚 EL FAVORITO", target: "Cría a tu criatura con todos los superpoderes desbloqueados.",
        features: [
          "🎨 Personalización Premium INCLUIDA: 4 skins exclusivos + auras neón (valor $4.99)",
          "📞 Llamadas a MITAD DE PRECIO — $0.10 USD el minuto (50% OFF)",
          "🎁 +10 minutos de voz de REGALO con cada activación",
          "🤖 300 Interacciones Automatizadas* al mes (15 veces más que Free)",
          "⚡ Prioridad en el radar: tu avatar hace match primero",
          "🏅 Insignia dorada PLUS en el muro y los foros",
          "🚀 Activación instantánea — sin requisito de invitaciones",
        ],
      },
    ],
    tooltip: "Una Interacción Automatizada = un diálogo completo IA-a-IA que tu avatar realiza por ti (negociación de 4 turnos + análisis de compatibilidad).",
    current: "PLAN ACTIVO", until: "hasta",
    payErr: "No se pudo abrir el pago. Intenta de nuevo.",
  },
};

export const PricingTable = ({ lang, user, setUser, onCta }) => {
  const t = P[lang] || P.en;
  const [tip, setTip] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showLegal, setShowLegal] = useState(false);

  const startCheckout = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { package_id: "tamagochy_30", origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || t.payErr);
      setBusy(false);
    }
  };

  const checkout = async (tier) => {
    if (tier.key === "free") {
      window.location.href = user ? "/dashboard" : "/register";
      return;
    }
    if (!user) {
      window.location.href = "/register";
      return;
    }
    if (onCta) return onCta(tier.key);
    if (!user.legal_accepted) {
      setShowLegal(true);  // legal consent happens AT PAYMENT time
      return;
    }
    startCheckout();
  };

  return (
    <div data-testid="pricing-table">
      {showLegal && (
        <LegalConsentModal lang={lang} user={user} setUser={setUser}
          onDone={() => { setShowLegal(false); startCheckout(); }}
          onCancel={() => setShowLegal(false)} />
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch max-w-3xl mx-auto">
        {t.tiers.map((tier) => {
          const popular = !!tier.popular;
          const current = user?.tier === "plus" ? tier.key === "plus" : user?.tier === "free" && tier.key === "free";
          return (
            <div key={tier.key} data-testid={`tier-card-${tier.key}`}
              className={`relative flex flex-col rounded-3xl p-7 border ${
                popular
                  ? "border-[#FFD700]/60 bg-gradient-to-b from-[#FFD700]/8 to-[#FF6EC7]/5 shadow-[0_0_40px_rgba(255,215,0,0.2)] animate-neon-pulse"
                  : "border-white/10 bg-white/5"
              }`}>
              {popular && (
                <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 text-[10px] font-black tracking-[0.2em] bg-gradient-to-r from-[#FFD700] to-[#FF6EC7] text-[#05050A] px-5 py-1.5 rounded-full whitespace-nowrap shadow-[0_0_18px_rgba(255,215,0,0.5)]">
                  {tier.popular}
                </span>
              )}
              {current && (
                <span className="absolute -top-3 right-4 text-[9px] font-bold tracking-widest bg-[#00FFAA] text-[#05050A] px-3 py-1 rounded-full" data-testid={`current-plan-${tier.key}`}>
                  {t.current}{tier.key === "plus" && user?.plus_expires_at ? ` · ${t.until} ${user.plus_expires_at.slice(0, 10)}` : ""}
                </span>
              )}
              <h3 className="font-heading font-black text-white text-base tracking-wide flex items-center gap-2">
                {tier.key === "plus" && <Egg size={17} className="text-[#FFD700]" />}
                {tier.name}
              </h3>
              <p className="text-[11px] text-white/40 mt-1 mb-4">{tier.target}</p>
              <p className="mb-5">
                <span className={`text-3xl font-black font-heading text-transparent bg-clip-text ${popular ? "bg-gradient-to-r from-[#FFD700] to-[#FF6EC7]" : "bg-gradient-to-r from-[#B026FF] to-[#00F5FF]"}`}>
                  {tier.price}
                </span>
                <span className="text-xs text-white/40 ml-1">{tier.period}</span>
              </p>
              <ul className="space-y-2.5 mb-7 flex-1">
                {tier.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-xs text-white/70 leading-relaxed">
                    <Check size={14} className={`mt-0.5 shrink-0 ${popular ? "text-[#FFD700]" : "text-[#00FFAA]"}`} /> {f}
                  </li>
                ))}
              </ul>
              <button data-testid={`tier-cta-${tier.key}`} onClick={() => checkout(tier)} disabled={busy || (current && tier.key === "plus")}
                className={`w-full py-4 rounded-full text-xs font-bold uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${
                  current && tier.key === "plus" ? "bg-white/5 text-white/30 border border-white/10"
                    : popular ? "bg-gradient-to-r from-[#FFD700] to-[#FF6EC7] text-[#05050A] shadow-[0_0_22px_rgba(255,215,0,0.45)] hover:shadow-[0_0_35px_rgba(255,215,0,0.7)] hover:-translate-y-0.5"
                    : "bg-white/10 hover:bg-white/15"
                }`}>
                {busy && tier.key === "plus" ? <Loader2 size={14} className="animate-spin" /> : null}
                {tier.cta}
              </button>
            </div>
          );
        })}
      </div>
      <p className="relative text-[10px] text-white/35 mt-6 text-center max-w-2xl mx-auto">
        <button onClick={() => setTip((v) => !v)} className="inline-flex items-center gap-1 underline decoration-dotted" data-testid="interaction-tooltip-btn">
          <Info size={11} /> *
        </button>{" "}
        {tip && <span data-testid="interaction-tooltip">{t.tooltip}</span>}
      </p>
    </div>
  );
};
